// Currency formatting chokepoint (design roadmap Round G): pass i18n.language
// so amounts read natively in every locale — never paste a `$${(cents / 100)
// .toFixed(2)}` template in page code. The admin app has no i18n and passes
// "en-US" explicitly. Machine formats (input values/max/state seeds) stay
// dot-decimal toFixed — those are parsed back, not displayed.

interface MoneyOptions {
  maximumFractionDigits?: number;
}

// Multi-currency variant (Stripe chargebacks arrive in arbitrary currencies).
export const formatMoney = (cents: number, currency: string, locale: string, opts?: MoneyOptions) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    ...(opts?.maximumFractionDigits !== undefined
      ? { maximumFractionDigits: opts.maximumFractionDigits, minimumFractionDigits: 0 }
      : {}),
  }).format(cents / 100);

// MAJOR units, not cents. Payout rails deliver amounts in the destination
// currency's own units — Wise returns 352.4 CNY, not 35240 — and the
// zero-decimal currencies (JPY, KRW, VND, CLP) have no minor unit at all, so
// dividing by 100 would show a Japanese tutor a hundredth of their payout.
// Use this for anything a rail delivered; use formatMoney for anything we
// store in cents.
export const formatCurrencyAmount = (amount: number, currency: string, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);

// The common case — all Jynio prices are USD today; the locale changes digit
// grouping and symbol placement, not the currency.
export const formatUsd = (cents: number, locale: string, opts?: MoneyOptions) =>
  formatMoney(cents, "USD", locale, opts);

// A four-week plan is paid 13 times a year, so its monthly equivalent is the
// payment × 13 / 12. Rounded UP to a whole dollar so the figure shown next to
// the real price never understates it (#1091).
export const fourWeekMonthlyEquivalentCents = (paymentCents: number) =>
  Math.ceil((paymentCents * 13) / 12 / 100) * 100;

// Whole-dollar display ("$25", "25 $US") for rate badges and price tags.
export const formatUsdWhole = (cents: number, locale: string) =>
  formatUsd(cents, locale, { maximumFractionDigits: 0 });
